import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import {
  defaultMetaObj,
  deleteErrorResponse,
  initialProjectPhaseState,
  phaseDeleteErrorModified,
} from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { projectPhaseEndPoint } = endPoints;

export const fetchAllProjectPhase = createAsyncThunk(
  "phases/fetchAllProjectPhase",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: projectPhaseEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectPhase = createAsyncThunk(
  "phases/deleteProjectPhase",
  async ({ phaseId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        projectPhaseEndPoint,
        phaseId
      );

      if (response.status === 204) {
        NotificationManager.success("Phase Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(
        errorMessage?.includes(deleteErrorResponse)
          ? phaseDeleteErrorModified
          : errorMessage
      );
      return rejectWithValue(error.message);
    }
  }
);

export const addNewProjectPhase = createAsyncThunk(
  "phases/addNewProjectPhase",
  async (newPhase: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${projectPhaseEndPoint}/`,
        newPhase
      );

      if (response.status === 201) {
        NotificationManager.success("Phase Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectPhase = createAsyncThunk(
  "phases/updateProjectPhase",
  async ({ updatedPhase, phaseId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        projectPhaseEndPoint,
        phaseId,
        updatedPhase
      );
      if (response.status === 204) {
        NotificationManager.success("Phase data updated successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const projectPhaseSlice = createSlice({
  name: "phases",
  initialState: initialProjectPhaseState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all phase
    builder
      .addCase(fetchAllProjectPhase.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllProjectPhase.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.phases = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllProjectPhase.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // delete Phase
    builder
      .addCase(deleteProjectPhase.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteProjectPhase.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteProjectPhase.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new phase
    builder
      .addCase(addNewProjectPhase.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewProjectPhase.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addNewProjectPhase.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update Phase
    builder
      .addCase(updateProjectPhase.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateProjectPhase.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateProjectPhase.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const projectPhaseReducer = projectPhaseSlice.reducer;
